/**
 * Address-specific Zod schemas
 * Extends base field schemas with address-specific validation
 */

import { z } from 'zod';
import {
    textSchema,
    postalCodeWithCountrySchema,
    phoneWithCountrySchema,
    countryCodeSchema,
    optionalCountryCodeSchema
} from './base-fields';

/**
 * Street address schema
 */
export const streetSchema = textSchema.min(1, 'Street address is required').max(100, 'Street address cannot exceed 100 characters');

/**
 * Optional street schema
 */
export const optionalStreetSchema = streetSchema.optional();

/**
 * City schema
 */
export const citySchema = textSchema
    .min(1, 'City is required')
    .max(50, 'City cannot exceed 50 characters')
    .regex(/^[a-zA-ZÀ-ÿ\s-']+$/, 'City can only contain letters, spaces, hyphens, and apostrophes');

/**
 * Optional city schema
 */
export const optionalCitySchema = citySchema.optional();

/**
 * State/Province schema
 */
export const stateSchema = textSchema.min(1, 'State is required').max(50, 'State cannot exceed 50 characters');

/**
 * Optional state schema
 */
export const optionalStateSchema = stateSchema.optional();

/**
 * County schema (optional by default, required for some countries like Italy)
 */
export const countySchema = textSchema.max(50, 'County cannot exceed 50 characters');

/**
 * Optional county schema
 */
export const optionalCountySchema = countySchema.optional();

/**
 * Required county schema (for countries like Italy)
 */
export const requiredCountySchema = countySchema.min(1, 'County is required');

/**
 * Base address schema with common fields
 */
export const baseAddressSchema = z.object({
    Name: textSchema.min(1, 'Name is required').max(34, 'Name must be 34 characters or less'),
    Street: streetSchema,
    City: citySchema,
    State: optionalStateSchema,
    ZipCode: textSchema.min(1, 'Postal code is required'),
    CountyName: optionalCountySchema,
    ThreeLetterISORegionName: countryCodeSchema,
    TwoLetterISORegionName: optionalCountryCodeSchema,
    Phone: textSchema.min(1, 'Phone number is required')
});

/**
 * Address schema with country-specific validation
 */
export const getAddressSchema = (locale?: string, countryCode?: string) => {
    const baseSchema = baseAddressSchema;

    switch (locale?.toLowerCase()) {
        case 'it':
        case 'enit':
            return baseSchema.extend({
                ZipCode: postalCodeWithCountrySchema('IT'),
                Phone: phoneWithCountrySchema('IT'),
                CountyName: requiredCountySchema
                    .min(2, 'County code must be 2 characters')
                    .max(2, 'County code must be 2 characters')
                    .regex(/^[A-Z]{2}$/, 'County code must be uppercase letters')
            });

        case 'us':
        case 'enus':
            return baseSchema.extend({
                ZipCode: postalCodeWithCountrySchema('US'),
                Phone: phoneWithCountrySchema('US'),
                State: stateSchema // Required for US
            });

        case 'gb':
        case 'engb':
            return baseSchema.extend({
                ZipCode: postalCodeWithCountrySchema('GB'),
                Phone: phoneWithCountrySchema('GB')
            });

        case 'fr':
        case 'enfr':
            return baseSchema.extend({
                ZipCode: postalCodeWithCountrySchema('FR'),
                Phone: phoneWithCountrySchema('FR')
            });

        case 'de':
        case 'ende':
            return baseSchema.extend({
                ZipCode: postalCodeWithCountrySchema('DE'),
                Phone: phoneWithCountrySchema('DE')
            });

        case 'au':
        case 'enau':
            return baseSchema.extend({
                ZipCode: postalCodeWithCountrySchema('AU'),
                Phone: phoneWithCountrySchema('AU')
            });

        case 'sg':
        case 'ensg':
            return baseSchema.extend({
                ZipCode: postalCodeWithCountrySchema('SG'),
                Phone: phoneWithCountrySchema('SG')
            });

        case 'hk':
        case 'enhk':
        case 'zhhk':
            return baseSchema.extend({
                ZipCode: postalCodeWithCountrySchema('HK'),
                Phone: phoneWithCountrySchema('HK')
            });

        case 'eu':
            // For EU regions, use flexible validation for multiple countries
            return baseSchema.extend({
                ZipCode: textSchema // Use basic text validation for EU postal codes
                    .min(3, 'Postal code is required')
                    .max(10, 'Postal code cannot exceed 10 characters'),
                Phone: phoneWithCountrySchema() // Use generic phone validation without country code
            });

        default:
            // Use country code if available for validation
            if (countryCode) {
                return baseSchema.extend({
                    ZipCode: postalCodeWithCountrySchema(countryCode),
                    Phone: phoneWithCountrySchema(countryCode)
                });
            }
            return baseSchema;
    }
};

/**
 * Billing address schema (usually same as shipping)
 */
export const billingAddressSchema = baseAddressSchema;

/**
 * Get billing address schema with locale-specific validation
 */
export const getBillingAddressSchema = getAddressSchema;

// Type inference
export type AddressFormData = z.infer<typeof baseAddressSchema>;
export type LocalizedAddressFormData = z.infer<ReturnType<typeof getAddressSchema>>;
